import React, { useState, useEffect } from 'react';
import AdminAlicuotas from './alicuotas/AdminAlicuotas';
import './AdminDashboard.css';
import ChatBubble from './components/chat/ChatBubble';
import UrbanIA from './components/UrbanIA/UrbanIA';
import UrbanIADisclaimer from './components/UrbanIA/UrbanIADisclaimer';
import {
  FaHome,
  FaFileInvoiceDollar,
  FaBell,
  FaUser,
  FaUsers,
  FaChartBar,
  FaCalendarAlt,
  FaEnvelope,
  FaChevronLeft,
  FaChevronRight,
  FaBars,
  FaTimes
} from 'react-icons/fa';
import NewsManager from './avisos/NewsManager';
import AdminContabilidad from './facturas/AdminContabilidad';
import Inicio from './inicio'; // Importamos el nuevo componente
import AdminSupport from './AdminSupport';
import { API_ENDPOINTS } from './config/api';
import UserManagement from './profile/UserManagement';
import BillingSection from './facturas/components/billing/BillingSection';
import AdminReservas from './reservas/AdminReservas';
import { useNavigate } from 'react-router-dom';

function AdminDashboard({ user, onLogout }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [iaDisclaimerDone, setIaDisclaimerDone] = useState(() => {
    const normalizedRole = (user?.role || 'admin').toLowerCase();
    return localStorage.getItem(`urbania_disclaimer_accepted_${normalizedRole}`) === 'true';
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setMobileSidebarOpen(false);
  };

  // Cerrar sidebar móvil al cambiar de sección
  const handleSectionChange = (section) => {
    setActiveSection(section);
    closeMobileSidebar();
  };


  const [activeSection, setActiveSection] = useState('inicio');
  const [userInfo, setUserInfo] = useState({
    nombre: user?.name || 'Usuario',
    apartamento: user?.apartment || 'A-303',
    ultimoPago: '15/03/2023',
    saldoPendiente: '$120.000'
  });
  
  const [notificaciones, setNotificaciones] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alicuotas, setAlicuotas] = useState([]);

  const [stats, setStats] = useState({
    pagosRealizados: 0,
    pagosPendientes: 0,
    total: 0,
    progreso: 0
  });
  
 
  // Función para obtener el token
  const getToken = () => {
    return localStorage.getItem('token') || '';
  };
  
  // Función para formatear la fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // Calcular saldo pendiente
  const calcularSaldoPendiente = () => {
    if (!pagos || pagos.length === 0) return userInfo.saldoPendiente;
    
    const saldo = pagos
      .filter(pago => pago.status === 'Pending')
      .reduce((total, pago) => total + parseFloat(pago.amount || 0), 0);
    
    return `$${saldo.toLocaleString()}`;
  };
  
  // Obtener fecha del último pago
  const obtenerFechaUltimoPago = () => {
    if (!pagos || pagos.length === 0) return userInfo.ultimoPago;
    
    const pagosPagados = pagos.filter(pago => pago.status === 'Paid');
    if (pagosPagados.length === 0) return 'No hay pagos registrados';
    
    // Ordenar por fecha de pago descendente
    pagosPagados.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));
    
    return formatDate(pagosPagados[0].payment_date);
  };
  
  // Función para añadir una nueva alícuota
  const addAlicuota = (newAlicuota) => {
    setAlicuotas(prevAlicuotas => [...prevAlicuotas, newAlicuota]);
  };
  
  // Función para eliminar una alícuota
  const deleteAlicuota = (id) => {
    setAlicuotas(prevAlicuotas => prevAlicuotas.filter(a => a.id !== id));
  };
  
  // Función para actualizar una alícuota
  const updateAlicuota = (updatedAlicuota) => {
    setAlicuotas(prevAlicuotas => 
      prevAlicuotas.map(a => a.id === updatedAlicuota.id ? updatedAlicuota : a)
    );
  };
  
  // Cargar datos al montar el componente
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Configuración de headers para las peticiones
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        };
        
        // Obtener alícuotas (pagos)
        try {
          const paymentsResponse = await fetch(API_ENDPOINTS.payments, {
            headers
          });
          
          if (paymentsResponse.ok) {
            const data = await paymentsResponse.json();
            // Mapear los pagos a formato de alícuotas
            const formattedAlicuotas = (data.payments || []).map(payment => ({
              id: payment.id,
              concepto: payment.concept,
              monto: payment.amount,
              fechaVencimiento: payment.created_at ? new Date(payment.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              descripcion: payment.description || '',
              apartamento: payment.apartment || 'Sin apartamento',
              usuario: payment.user_name || payment.username || 'Usuario',
              estado: payment.status === 'Paid' ? 'Pagado' : 'Pendiente'
            }));
            setAlicuotas(formattedAlicuotas);
          }
        } catch (error) {
          console.error('Error al cargar alícuotas:', error);
        }
        
        // Obtener avisos
        try {
          const noticesResponse = await fetch(API_ENDPOINTS.notices, {
            method: 'GET',
            headers
          });
          
          if (noticesResponse.ok) {
            const noticesData = await noticesResponse.json();
            // Mapear los datos para que coincidan con la estructura esperada
            const formattedNotices = (noticesData.notices || []).map(notice => ({
              id: notice.id,
              titulo: notice.title,
              fecha: formatDate(notice.created_at),
              descripcion: notice.description
            }));
            
            setNotificaciones(formattedNotices);
          } else {
            console.error('Error al obtener avisos:', await noticesResponse.text());
          }
        } catch (e) {
          console.error('Error al conectar con el endpoint de avisos:', e);
          // Mantener datos de muestra si hay error
          setNotificaciones([
            {
              id: 1,
              titulo: 'Mantenimiento de ascensores',
              fecha: '12/04/2023',
              descripcion: 'Se realizará mantenimiento de los ascensores el día 15 de abril de 8:00 AM a 12:00 PM.'
            },
            {
              id: 2,
              titulo: 'Asamblea general de residentes',
              fecha: '05/04/2023',
              descripcion: 'Se convoca a todos los residentes a la asamblea general el día 20 de abril a las 7:00 PM en el salón comunal.'
            },
            {
              id: 3,
              titulo: 'Pago de administración',
              fecha: '01/04/2023',
              descripcion: 'Recordamos a todos los residentes que el pago de la administración debe realizarse antes del día 10 de cada mes.'
            }
          ]);
        }
        
        // Obtener pagos del usuario
        try {
          const paymentsResponse = await fetch(API_ENDPOINTS.paymentsUser, {
            method: 'GET',
            headers
          });
          
          if (paymentsResponse.ok) {
            const paymentsData = await paymentsResponse.json();
            setPagos(paymentsData.payments || []);
            
            // Actualizar estadísticas
            const paymentsArray = paymentsData.payments || [];
            const total = paymentsArray.length;
            const pagados = paymentsArray.filter(p => p.status === 'Paid').length;
            const pendientes = total - pagados;
            const progreso = total > 0 ? Math.round((pagados / total) * 100) : 0;
            
            setStats({
              pagosRealizados: pagados,
              pagosPendientes: pendientes,
              total,
              progreso
            });
            
            // Si hay pagos, actualizar el saldo pendiente y último pago en userInfo
            if (paymentsArray.length > 0) {
              setUserInfo(prevInfo => ({
                ...prevInfo,
                ultimoPago: obtenerFechaUltimoPago(),
                saldoPendiente: calcularSaldoPendiente()
              }));
            }
          } else {
            console.error('Error al obtener pagos:', await paymentsResponse.text());
          }
        } catch (e) {
          console.error('Error al conectar con el endpoint de pagos:', e);
          // Mantener stats de muestra si hay error
          setStats({
            pagosRealizados: 4,
            pagosPendientes: 1,
            total: 5,
            progreso: 80
          });
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error general al cargar datos:', err);
        setError('Ocurrió un error al cargar los datos. Por favor intente nuevamente.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user?.id]);
  
  // Renderizar la sección activa
  const renderSection = () => {
    if (loading) {
      return <div className="loading-spinner">Cargando...</div>;
    }
    
    if (error) {
      return <div className="error-message">{error}</div>;
    }
    
    switch(activeSection) {
      case 'alicuotas':
        return (
          <AdminAlicuotas 
            user={user}
            alicuotas={alicuotas} 
            onAddAlicuota={addAlicuota}
            onDeleteAlicuota={deleteAlicuota}
            onUpdateAlicuota={updateAlicuota}
          />
        );
        case 'avisos':
          return (
            <NewsManager 
              user={user} 
              onNoticeCreated={(newNotice) => {
                // Actualizar el estado local de notificaciones si es necesario
                setNotificaciones(prevNoti => [...prevNoti, newNotice]);
              }}
            />
          );
case 'contabilidad': 
  return (
    <AdminContabilidad 
      user={user} 
      alicuotas={alicuotas} 
      pagos={pagos}
      setActiveSection={setActiveSection}  // ← Agregar esta línea
    />
  );
case 'facturacion':
  return <BillingSection user={user} />;
case 'reservas':
  return <AdminReservas user={user} />;
case 'usuarios':
  return <UserManagement user={user} />;

          case 'ingreso':
           return <AdminSupport user={user} />;
        
        case 'inicio':
          default:
            return (
              <Inicio 
                user={user}
                notificaciones={notificaciones}
                pagos={pagos} // Asegúrate de que tienes este estado definido
                alicuotas={alicuotas}
                stats={stats} // Opcional: también puedes calcular estas estadísticas en Inicio.js
                setActiveSection={setActiveSection}
              />
            );




    }
  };
  
  return (
    <div className="admin-dashboard-container">
      {/* Botón hamburguesa para móvil */}
      <button className="sidebar-mobile-toggle" onClick={toggleMobileSidebar}>
        {mobileSidebarOpen ? <FaTimes /> : <FaBars />}
      </button>

      {/* Overlay para cerrar sidebar en móvil */}
      <div
        className={`sidebar-overlay ${mobileSidebarOpen ? 'active' : ''}`}
        onClick={closeMobileSidebar}
      />

      {/* Sidebar con clases responsive */}
      <div className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'open' : ''}`}>
        {/* Botón de toggle (desktop) */}
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>

        <div className="sidebar-header">
          <h2>Panel Admin</h2>
        </div>
        <ul className="sidebar-menu">
          <li
            data-section="inicio"
            data-tooltip="Dashboard"
            className={activeSection === 'dashboard' || activeSection === 'inicio' ? 'active' : ''}
            onClick={() => handleSectionChange('inicio')}
          >
            <FaHome /> <span>Dashboard</span>
          </li>
          <li
            data-section="alicuotas"
            data-tooltip="Alícuotas"
            className={activeSection === 'alicuotas' ? 'active' : ''}
            onClick={() => handleSectionChange('alicuotas')}
          >
            <FaFileInvoiceDollar /> <span>Alícuotas</span>
          </li>
          <li
            data-section="usuarios"
            data-tooltip="Usuarios"
            className={activeSection === 'usuarios' ? 'active' : ''}
            onClick={() => handleSectionChange('usuarios')}
          >
            <FaUsers /> <span>Usuarios</span>
          </li>
          <li
            data-section="avisos"
            data-tooltip="Avisos"
            className={activeSection === 'avisos' ? 'active' : ''}
            onClick={() => handleSectionChange('avisos')}
          >
            <FaBell /> <span>Avisos</span>
            {notificaciones.length > 0 && <span className="badge">{notificaciones.length}</span>}
          </li>
          <li
            data-section="contabilidad"
            data-tooltip="Contabilidad"
            className={activeSection === 'contabilidad' ? 'active' : ''}
            onClick={() => handleSectionChange('contabilidad')}
          >
            <FaChartBar /> <span>Contabilidad</span>
          </li>
          <li
            data-section="facturacion"
            data-tooltip="Facturación"
            className={activeSection === 'facturacion' ? 'active' : ''}
            onClick={() => handleSectionChange('facturacion')}
          >
            <FaChartBar /> <span>Facturación</span>
          </li>
          <li
            data-section="reservas"
            data-tooltip="Reservas"
            className={activeSection === 'reservas' ? 'active' : ''}
            onClick={() => handleSectionChange('reservas')}
          >
            <FaCalendarAlt /> <span>Reservas</span>
          </li>
          <li
            data-section="ingreso"
            data-tooltip="Soporte"
            className={activeSection === 'ingreso' ? 'active' : ''}
            onClick={() => handleSectionChange('ingreso')}
          >
            <FaUser /> <span>Soporte</span>
          </li>
        </ul>
      </div>

      {/* Main con clase expanded */}
      <div className={`dashboard-main ${sidebarCollapsed ? 'expanded' : ''}`}>
        <div className="dashboard-content">
          {renderSection()}
        </div>
      </div>

      {/* Chat flotante */}
      {user && <ChatBubble user={user} />}

      {/* UrbanIA - Asistente Inteligente */}
      {!iaDisclaimerDone && (
        <UrbanIADisclaimer
          userName={user?.name || 'Administrador'}
          role={user?.role || 'Admin'}
          onAccept={() => setIaDisclaimerDone(true)}
        />
      )}
      {iaDisclaimerDone && <UrbanIA user={user} />}
    </div>
  );
}

export default AdminDashboard;